import React, { useMemo, useState, useEffect } from 'react'
import BookingCurveChart from './BookingCurveChart'
import RoomPaceTable from './RoomPaceTable'
import RateWaterfallChart from './RateWaterfallChart'
import RateHistoryChart from './RateHistoryChart'
import LeadTimeChart from './LeadTimeChart'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { BookingCurveData, OccupancyDay, RoomType, RateData, AdrDay, RateHistoryEntry } from '@/types/calendar'
import { cn } from '@/lib/utils'
import { currencySymbol, formatRateSym, nightsUntil } from '@/lib/calendar-utils'
import { paceBand, paceBandWithReference, paceComparison, formatPickupRooms, type Tone } from '@/lib/plain-language'
import EngineDetails from '@/components/EngineDetails'
import { useFigureScope } from '@/lib/figure-scope'

interface DayDetailInsightsProps {
  date: string
  curveData: BookingCurveData | null
  curveLoading: boolean
  occupancy?: OccupancyDay
  roomTypes: RoomType[]
  rates: Record<string, RateData>
  rateHistory?: Record<string, RateHistoryEntry[]>
  adrData?: Record<string, AdrDay>
  currency: string
  /** Today AT THE PROPERTY (YYYY-MM-DD) — see calendar-utils#todayIso. */
  today: string
  selectedRoomTypeId?: number | null
  /** True when rooms display prices from DIFFERENT rate plans — the Rate Pipeline
   *  heading then names the active room's plan (uniform case is covered by the
   *  modal header chip). */
  mixedPlans?: boolean
}

/** Pickup chip — pickup is ROOMS (locked decision), never percentage points. */
function PickupStat({ rooms, period }: { rooms: number | null; period: string }) {
  const isPositive = rooms != null && rooms > 0
  const isNegative = rooms != null && rooms < 0
  return (
    <div className={cn(
      'flex items-center gap-1.5 rounded-lg px-3 py-2',
      isPositive ? 'bg-emerald-50 border border-emerald-200' :
      isNegative ? 'bg-rose-50 border border-rose-200' :
      'bg-slate-50 border border-slate-200'
    )}>
      <span className={cn(
        'text-sm font-semibold tabular-nums',
        isPositive ? 'text-emerald-700' :
        isNegative ? 'text-rose-700' :
        'text-slate-500'
      )}>
        {formatPickupRooms(rooms)}
      </span>
      <span className="text-[11px] text-muted-foreground">{period}</span>
    </div>
  )
}

// Worded-pace card tones — the card colour follows the five-band scale, never a ratio.
const PACE_CARD_TONE: Record<Tone, string> = {
  positive: 'border-emerald-200 bg-emerald-50',
  neutral: 'border-border/60 bg-slate-50',
  negative: 'border-rose-200 bg-rose-50',
}
const PACE_TEXT_TONE: Record<Tone, string> = {
  positive: 'text-emerald-700',
  neutral: 'text-foreground',
  negative: 'text-rose-700',
}

function StatCard({ label, value, subValue, positive }: {
  label: string
  value: string
  subValue?: string
  positive?: boolean | null
}) {
  return (
    <div className="rounded-lg border border-border/40 px-4 py-3 flex-1 min-w-0">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={cn(
        'text-lg font-bold tabular-nums mt-0.5',
        positive === true ? 'text-emerald-700' :
        positive === false ? 'text-rose-700' :
        'text-foreground'
      )}>{value}</p>
      {subValue && <p className="text-[11px] text-muted-foreground mt-0.5">{subValue}</p>}
    </div>
  )
}

function yoyChange(current: number | null | undefined, lastYear: number | null | undefined): string | undefined {
  if (current == null || lastYear == null || lastYear === 0) return undefined
  const pct = ((current - lastYear) / lastYear * 100).toFixed(0)
  return `${Number(pct) >= 0 ? '+' : ''}${pct}% vs last year`
}

export default function DayDetailInsights({
  date,
  curveData,
  curveLoading,
  occupancy,
  roomTypes,
  rates,
  rateHistory,
  adrData,
  currency,
  selectedRoomTypeId,
  today,
  mixedPlans,
}: DayDetailInsightsProps) {
  const sym = currencySymbol(currency)
  // "Building ADR" at a combined inventory (server label), "ADR" everywhere else.
  const { adrLabel } = useFigureScope()
  const currentLeadTime = Math.max(0, nightsUntil(date, today))

  // Default to the pre-selected room type from the calendar, fallback to first with data
  const defaultRtId = useMemo(() => {
    if (selectedRoomTypeId && roomTypes.some((rt) => rt.id === selectedRoomTypeId)) return selectedRoomTypeId
    for (const rt of roomTypes) {
      if (rates[`${rt.id}:${date}`]) return rt.id
    }
    return roomTypes[0]?.id
  }, [selectedRoomTypeId, roomTypes, rates, date])

  const [activeRtId, setActiveRtId] = useState<number | undefined>(defaultRtId)

  // Sync when date or selected room changes
  useEffect(() => { setActiveRtId(defaultRtId) }, [defaultRtId])

  const activeRt = roomTypes.find((rt) => rt.id === activeRtId) ?? roomTypes[0]
  const activeRate = activeRt ? rates[`${activeRt.id}:${date}`] : undefined
  const activeHistory = activeRt && rateHistory ? rateHistory[`${activeRt.id}:${date}`] : undefined
  const adr = adrData?.[date]
  const revpar = curveData?.revpar
  const segBaselines = curveData?.segmentBaselines

  let delay = 0
  const nextDelay = () => { const d = delay; delay += 50; return `${d}ms` }

  return (
    <div className="space-y-6">
      {/* Room type selector */}
      {roomTypes.length > 1 && (
        <div className="px-6">
          <Select value={String(activeRtId)} onValueChange={(v) => setActiveRtId(Number(v))}>
            <SelectTrigger className="w-[240px] h-8 text-xs">
              <SelectValue placeholder="Select room type" />
            </SelectTrigger>
            <SelectContent>
              {roomTypes.map((rt) => (
                <SelectItem key={rt.id} value={String(rt.id)} className="text-xs">
                  {rt.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Row 1: Pace + Revenue KPIs */}
      <div className="animate-slide-in px-6 grid grid-cols-2 gap-4" style={{ animationDelay: nextDelay() }}>
        {/* Pace card — worded five-band scale, never a bare multiplier. The raw
            ratio / occupancy / baseline live one click away in EngineDetails. */}
        {(() => {
          const paceRatio = curveData?.property.paceRatio ?? null
          const occPct = curveData?.property.occupancyPct ?? null
          const baselinePct = curveData?.property.baselineOccPct ?? null
          const band = paceBand(paceRatio)
          const isUnknown = band.key === 'unknown'
          const segBaseline = segBaselines
            ? (curveData?.isWeekend ? segBaselines.weekend : segBaselines.weekday)?.expectedOccPct ?? null
            : null
          // WHAT "ON TRACK" IS ON TRACK FOR, in rooms. The day-type baseline is used when we have
          // one — and only then is the reference NAMED as a weekday/weekend, because that is the
          // only figure that supports the claim; otherwise the all-days baseline speaks
          // unattributed. `occupancy.available` is the night's capacity, backend-sent.
          const referenced = paceBandWithReference(band, {
            baselinePct: segBaseline ?? baselinePct,
            capacity: occupancy?.available ?? null,
            dayName: segBaseline != null ? (curveData?.isWeekend ? 'weekend' : 'weekday') : null,
          })
          // With a rooms reference on the card, the comparison drops its own percentage baseline:
          // stating the same expectation twice, in two units and off two different baselines (the
          // all-days one here, the day-type one below), is the ambiguity this change exists to end.
          const comparison = paceComparison(occPct, referenced.reference ? null : baselinePct)
          return (
            <div className={cn('group/row relative rounded-xl border px-5 py-4', PACE_CARD_TONE[band.tone])}>
              {/* Raw pace ratio / baselines — icon-only affordance in the card corner. */}
              <span className="absolute right-3 top-3">
                <EngineDetails variant="icon">
                  <EngineDetails.Row label="Pace ratio" value={paceRatio != null ? `${paceRatio.toFixed(2)}x` : '—'} />
                  <EngineDetails.Row label="Property occupancy" value={occPct != null ? `${occPct.toFixed(0)}%` : '—'} />
                  <EngineDetails.Row label="Baseline" value={baselinePct != null ? `${baselinePct.toFixed(0)}%` : '—'} />
                  {segBaseline != null && (
                    <EngineDetails.Row
                      label={curveData?.isWeekend ? 'Weekend baseline' : 'Weekday baseline'}
                      value={`${segBaseline.toFixed(0)}%`}
                    />
                  )}
                </EngineDetails>
              </span>
              <span className={cn(
                'text-2xl font-bold',
                isUnknown ? 'text-muted-foreground' : PACE_TEXT_TONE[band.tone],
              )}>
                {band.label}
              </span>
              {comparison && (
                <p className="text-[11px] text-muted-foreground mt-0.5">{comparison}</p>
              )}
              {/* The reference the band was judged against, in ROOMS — it replaces the old
                  percentage restatement of the same baseline rather than sitting beside it.
                  Falls back to that line whenever the night's capacity is not on the page. */}
              {referenced.reference ? (
                <p className="text-[10px] text-muted-foreground mt-1">{referenced.reference}</p>
              ) : (
                segBaseline != null && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {curveData?.isWeekend ? 'Weekends' : 'Weekdays'} usually ~{Math.round(segBaseline)}% full by now
                  </p>
                )
              )}
            </div>
          )
        })()}

        {/* Revenue KPIs */}
        <div className="flex gap-3">
          <StatCard
            label="RevPAR"
            value={revpar?.revpar != null ? formatRateSym(revpar.revpar, sym) : '—'}
            subValue={yoyChange(revpar?.revpar, revpar?.lastYearRevpar)}
            positive={revpar?.revpar != null && revpar?.lastYearRevpar != null ? revpar.revpar >= revpar.lastYearRevpar : null}
          />
          <StatCard
            label={adrLabel ?? 'ADR'}
            value={adr?.adr != null ? formatRateSym(adr.adr, sym) : revpar?.adr != null ? formatRateSym(revpar.adr, sym) : '—'}
            subValue={yoyChange(adr?.adr ?? revpar?.adr, adr?.lastYearAdr ?? revpar?.lastYearAdr)}
            positive={adr?.adr != null && adr?.lastYearAdr != null ? adr.adr >= adr.lastYearAdr : null}
          />
        </div>
      </div>

      {/* Row 2: Pickup Stats */}
      {occupancy && (
        <div className="animate-slide-in px-6" style={{ animationDelay: nextDelay() }}>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Occupancy Pickup
          </h4>
          <div className="flex items-center gap-3">
            <PickupStat rooms={occupancy.pickup7dRooms} period="7 days" />
            <PickupStat rooms={occupancy.pickup30dRooms} period="30 days" />
            <PickupStat rooms={occupancy.pickup60dRooms} period="60 days" />
          </div>
        </div>
      )}

      {/* Row 3: Rate Waterfall + Rate History (side by side) */}
      <div className="animate-slide-in px-6 grid grid-cols-2 gap-4" style={{ animationDelay: nextDelay() }}>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Rate Pipeline{activeRt ? ` · ${activeRt.name}` : ''}
            {mixedPlans && activeRate?.ratePlanName ? ` · ${activeRate.ratePlanName}` : ''}
          </h4>
          {activeRate?.computationDetails?.adjustments ? (
            <RateWaterfallChart
              adjustments={activeRate.computationDetails.adjustments}
              currency={currency}
            />
          ) : (
            <div className="flex items-center justify-center h-[180px] text-xs text-muted-foreground">
              No computation data
            </div>
          )}
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Rate History{activeRt ? ` · ${activeRt.name}` : ''}
          </h4>
          <RateHistoryChart
            history={activeHistory ?? []}
            currency={currency}
          />
        </div>
      </div>

      {/* Row 4: Booking Curve */}
      <div className="animate-slide-in px-6" style={{ animationDelay: nextDelay() }}>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Booking Curve
        </h4>
        {curveLoading ? (
          <div className="h-[200px] rounded-xl border border-border/60 bg-slate-50 animate-pulse" />
        ) : curveData ? (
          <BookingCurveChart
            data={curveData}
            currentLeadTime={currentLeadTime}
          />
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-white p-8 text-center shadow-sm">
            <p className="text-sm font-medium text-foreground">No booking curve data</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Data will appear once booking snapshots accumulate for this date.
            </p>
          </div>
        )}
      </div>

      {/* Row 5: Lead Time Distribution */}
      {curveData && !curveLoading && curveData.leadTimeDistribution?.length > 0 && (
        <div className="animate-slide-in px-6" style={{ animationDelay: nextDelay() }}>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Booking Lead Times
          </h4>
          <LeadTimeChart
            distribution={curveData.leadTimeDistribution}
            currentLeadTime={currentLeadTime}
          />
        </div>
      )}

      {/* Row 6: Room Type Pace */}
      {curveData && !curveLoading && (
        <div className="animate-slide-in px-6" style={{ animationDelay: nextDelay() }}>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Room Type Pace
          </h4>
          <RoomPaceTable
            roomTypes={curveData.roomTypes}
            property={curveData.property}
          />
        </div>
      )}
    </div>
  )
}
